import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { useCart } from './CartContext';
import { shopApi } from '../services/api';
import { C, fmt } from './ShopLayout';

const STEPS = ['Košík', 'Kontakt', 'Doprava & platba', 'Hotovo'];

function ProgressBar({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32 }}>
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: i <= step ? C.primary : '#e0e0e0', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>
              {i < step ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 11, color: i === step ? C.primary : C.muted, fontWeight: i === step ? 600 : 400, whiteSpace: 'nowrap' }}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? C.primary : '#e0e0e0', margin: '0 6px', marginBottom: 18 }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{label}{required && ' *'}</label>
      {children}
    </div>
  );
}

const inputStyle = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' };

export default function ShopCheckout() {
  const { settings } = useOutletContext();
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [carrier, setCarrier] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [branch, setBranch] = useState(null);
  const [address, setAddress] = useState({ street: '', city: '', zip: '' });
  const [payment, setPayment] = useState('transfer');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');

  useEffect(() => {
    shopApi.carriers().then(list => { setCarriers(list); if (list.length) setCarrier(list[0]); }).catch(() => {});
  }, []);

  const discountAmount = locationState.discountAmount || 0;
  const discountCode   = locationState.discountCode || '';
  const shipping = subtotal >= (settings.free_shipping || 1500) ? 0 : (carrier?.price ?? settings.shipping_cost ?? 89);
  const total = Math.max(0, subtotal - discountAmount) + shipping;

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function validateStep0() {
    if (!form.name.trim()) return 'Vyplňte jméno a příjmení';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) return 'Zadejte platný email';
    if (!form.phone.trim()) return 'Zadejte telefon';
    return '';
  }
  function validateStep1() {
    if (!carrier) return 'Vyberte způsob dopravy';
    if (carrier.requires_branch && !branch) return 'Vyberte výdejní místo';
    if (carrier.code === 'ppl' && (!address.street || !address.city || !address.zip)) return 'Vyplňte doručovací adresu';
    return '';
  }

  function nextStep() {
    const err = step === 0 ? validateStep0() : step === 1 ? validateStep1() : '';
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  }

  async function submit() {
    setSending(true); setError('');
    try {
      const customerAddress = carrier?.code === 'ppl'
        ? `${address.street}, ${address.zip} ${address.city}`
        : carrier?.code === 'pickup'
          ? settings.pickup_address || 'Osobní odběr'
          : branch?.name || '';

      const result = await shopApi.order({
        customer_name:    form.name,
        customer_email:   form.email,
        customer_phone:   form.phone,
        customer_address: customerAddress,
        customer_city:    address.city || '',
        customer_zip:     address.zip  || '',
        carrier_code:     carrier?.code,
        branch_id:        branch?.id   || '',
        branch_name:      branch?.name || '',
        payment_method:   payment,
        discount_code:    discountCode,
        items: items.map(i => ({ id: i.id, qty: i.qty, variant: i.variant })),
      });
      clear();
      setOrderId(result.order_id);
      setOrderNumber(result.order_number);
      setStep(3);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Nepodařilo se odeslat objednávku');
    } finally { setSending(false); }
  }

  if (items.length === 0 && step < 3) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ fontSize: 18, color: C.muted, marginBottom: 20 }}>Košík je prázdný</div>
      <Link to="/shop" style={{ color: C.primary }}>← Zpět do obchodu</Link>
    </div>
  );

  const Summary = () => (
    <div style={{ background: '#fafafa', borderRadius: 12, border: `0.5px solid ${C.border}`, padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 14 }}>Souhrn</div>
      {items.map(i => (
        <div key={`${i.id}-${i.variant}`} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40, background: '#eee', borderRadius: 6, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {i.image_url ? <img src={i.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📦'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{i.name}</div>
            {i.variant && <div style={{ fontSize: 11, color: C.muted }}>{i.variant}</div>}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.primary, flexShrink: 0 }}>
            {fmt(i.price * i.qty)}
          </div>
        </div>
      ))}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted }}><span>Zboží</span><span>{fmt(subtotal)}</span></div>
        {discountAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.teal }}><span>Sleva</span><span>−{fmt(discountAmount)}</span></div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted }}><span>Doprava</span><span style={{ color: shipping === 0 ? C.teal : C.text }}>{shipping === 0 ? 'zdarma' : fmt(shipping)}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: C.primary, marginTop: 4 }}><span>Celkem</span><span>{fmt(total)}</span></div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 24px 60px' }}>
      <ProgressBar step={step < 3 ? step : 2} />

      {step === 3 ? (
        // Potvrzení
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, background: C.teal, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 32 }}>✓</div>
          <h2 style={{ fontSize: 24, fontWeight: 500, marginBottom: 8 }}>Objednávka přijata!</h2>
          <p style={{ fontSize: 15, color: C.muted, marginBottom: 24 }}>
            Díky, <strong>{form.name}</strong>. Na email <strong>{form.email}</strong> jsme poslali proforma fakturu.
          </p>
          <div style={{ background: '#fafafa', borderRadius: 12, border: `0.5px solid ${C.border}`, padding: 24, marginBottom: 32, textAlign: 'left' }}>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>Číslo objednávky</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 20 }}>#{orderNumber}</div>
            {[
              ['Objednávka přijata', 'Proforma faktura odeslána emailem', true],
              ['Platba spárována', 'Potvrdíme SMS po přijetí platby', false],
              ['Připraveno k expedici', '', false],
              ['Odesláno', 'SMS se sledovacím číslem', false],
            ].map(([label, note, done]) => (
              <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: done ? C.teal : '#e0e0e0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: done ? 'white' : C.muted, marginTop: 1 }}>
                  {done ? '✓' : ''}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: done ? 500 : 400, color: done ? C.text : C.muted }}>{label}</div>
                  {note && <div style={{ fontSize: 12, color: C.muted }}>{note}</div>}
                </div>
              </div>
            ))}
          </div>
          <Link to="/shop" style={{ display: 'inline-block', padding: '11px 28px', background: C.primary, color: 'white', borderRadius: 8, textDecoration: 'none', fontWeight: 500 }}>
            Zpět do obchodu
          </Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 28, alignItems: 'start' }}>
          <div style={{ background: 'white', borderRadius: 12, border: `0.5px solid ${C.border}`, padding: 28 }}>

            {/* Krok 0 — Kontakt */}
            {step === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Kontaktní údaje</h2>
                <Field label="Jméno a příjmení" required>
                  <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jan Novák" />
                </Field>
                <Field label="Email" required>
                  <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jan@email.cz" />
                </Field>
                <Field label="Telefon" required>
                  <input style={inputStyle} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+420 777 123 456" />
                </Field>
              </div>
            )}

            {/* Krok 1 — Doprava & platba */}
            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Způsob dopravy</h2>
                {carriers.map(c => (
                  <div key={c.id} onClick={() => { setCarrier(c); setBranch(null); }}
                    style={{ padding: '14px 16px', border: `1.5px solid ${carrier?.id === c.id ? C.primary : C.border}`, borderRadius: 10, cursor: 'pointer', background: carrier?.id === c.id ? '#FEF3ED' : 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14, color: carrier?.id === c.id ? C.primary : C.text }}>{c.name}</div>
                      {c.code === 'zasilkovna' && branch && <div style={{ fontSize: 12, color: C.teal, marginTop: 2 }}>📍 {branch.name}</div>}
                      {c.code === 'pickup' && settings.pickup_address && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{settings.pickup_address}</div>}
                    </div>
                    <span style={{ fontWeight: 600, color: c.price === 0 ? C.teal : C.text, fontSize: 14 }}>
                      {c.price === 0 ? 'Zdarma' : fmt(c.price)}
                    </span>
                  </div>
                ))}

                {carrier?.requires_branch && (
                  <button onClick={() => alert('Zásilkovna widget — vyžaduje packeta.js z CDN. Nastavte API klíč v nastavení.')}
                    style={{ padding: '10px 20px', background: branch ? '#EAF3DE' : C.primary, color: branch ? '#3B6D11' : 'white', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 500 }}>
                    {branch ? `📍 ${branch.name}` : '📍 Vybrat výdejní místo'}
                  </button>
                )}

                {carrier?.code === 'ppl' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h2 style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>Doručovací adresa</h2>
                    <Field label="Ulice a číslo" required><input style={inputStyle} value={address.street} onChange={e => setAddress(a => ({ ...a, street: e.target.value }))} /></Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                      <Field label="Město" required><input style={inputStyle} value={address.city} onChange={e => setAddress(a => ({ ...a, city: e.target.value }))} /></Field>
                      <Field label="PSČ" required><input style={inputStyle} value={address.zip} onChange={e => setAddress(a => ({ ...a, zip: e.target.value }))} /></Field>
                    </div>
                  </div>
                )}

                <h2 style={{ fontSize: 16, fontWeight: 500, margin: '8px 0 0' }}>Způsob platby</h2>
                {[['transfer','Bankovní převod'],['cod','Dobírka']].map(([val, label]) => (
                  <div key={val} onClick={() => setPayment(val)}
                    style={{ padding: '12px 16px', border: `1.5px solid ${payment === val ? C.primary : C.border}`, borderRadius: 10, cursor: 'pointer', background: payment === val ? '#FEF3ED' : 'white' }}>
                    <div style={{ fontWeight: 500, fontSize: 14, color: payment === val ? C.primary : C.text }}>{label}</div>
                    {val === 'transfer' && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Po odeslání objednávky dostanete proforma fakturu s platebními údaji</div>}
                    {val === 'cod' && carrier?.cod_extra > 0 && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>+{fmt(carrier.cod_extra)} k ceně dopravy</div>}
                  </div>
                ))}
              </div>
            )}

            {error && <div style={{ background: '#fef2f2', color: '#A32D2D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 16 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              {step > 0 && <button onClick={() => { setStep(s => s-1); setError(''); }} style={{ padding: '11px 20px', border: `1.5px solid ${C.border}`, background: 'white', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>← Zpět</button>}
              {step < 1
                ? <button onClick={nextStep} style={{ flex: 1, padding: '11px 0', background: C.primary, color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>Pokračovat →</button>
                : <button onClick={submit} disabled={sending} style={{ flex: 1, padding: '11px 0', background: C.primary, color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>
                    🔒 {sending ? 'Odesílám…' : `Objednat · ${fmt(total)}`}
                  </button>
              }
            </div>
          </div>

          <div style={{ position: 'sticky', top: 84 }}><Summary /></div>
        </div>
      )}
    </div>
  );
}
